import {
  ShopOrderCashReceiptPendingItem,
  ShopOrderCashReceiptPendingResponse,
} from '@shared/api/shopOrder/shopOrder.interface';
import { paymentStatusLabel } from '@shared/utils/orderStatusLabel';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

// 현금영수증 발행 대기 큐 엑셀 — 홈택스 입력·월 대사용 작업지시서.
// 프로젝트 표준(커뮤니티 xlsx + file-saver)만 쓰고, 스타일 대신 너비·고정·필터·숫자포맷으로 읽히게 한다.
//
// 전화번호를 문자열로 강제하는 이유: 010…은 숫자로 들어가면 앞 0이 잘려 홈택스에
// 그대로 붙여넣을 수 없다. 이 파일의 존재 이유가 "행만 보고 바로 치는 것"이라 치명적이다.

const p = (n: number) => String(n).padStart(2, '0');

const fmtDate = (date: Date | string | undefined) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

//* 기한일 = 결제일 + n일. 화면과 같은 기준(안전 5일 / 최대 30일)을 열로 박아
//* 엑셀만 보고 처리하는 사람도 언제까지인지 날짜로 알 수 있게 한다.
const plusDays = (date: Date | string | undefined, days: number) => {
  if (!date) return '';
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return fmtDate(d);
};

const deadlineText = (waitingDays: number, safeDays: number, maxDays: number) => {
  if (waitingDays <= safeDays) return `D-${safeDays - waitingDays}`;
  if (waitingDays <= maxDays) return `${waitingDays - safeDays}일 지연`;
  return `최대기한 +${waitingDays - maxDays}일`;
};

export const exportCashReceiptQueueExcel = (
  items: ShopOrderCashReceiptPendingItem[],
  meta: Pick<ShopOrderCashReceiptPendingResponse, 'range'> & {
    safeDeadlineDays: number;
    maxDeadlineDays: number;
  },
) => {
  const rows = items.map((item) => ({
    주문번호: item.orderNo,
    결제일: fmtDate(item.purchasedAt),
    //* 발행액이 결제 전액인지 교체 차액인지 — 엑셀만 보고 치는 사람이 가장 헷갈릴 지점
    구분: item.kind === 'itemChange' ? '교체차액' : '계좌입금',
    발행금액: item.amount,
    //*! 교체차액 건은 발행금액 ≠ 결제총액이다. 결제총액을 홈택스에 치면 PG가 이미
    //*! 발행한 원결제분까지 이중 발행된다 — 대사용으로만 담는다.
    결제총액: item.paymentByCash,
    교체일: item.kind === 'itemChange' ? fmtDate(item.lastChangedAt) : '',
    //* 적립금 사용분은 현금이 아니라 발행 대상이 아니다 — 대사할 때 눈으로 확인하는 참고값
    포인트사용: item.paymentByPoint || '',
    주문자: item.senderName ?? '',
    //* 현금영수증 식별정보 (별도 필드 없이 주문자 번호를 그대로 쓴다)
    전화번호: item.senderPhoneNumber ?? '',
    상품: item.itemName ?? '',
    수량: item.quantity ?? '',
    대기일수: item.waitingDays,
    안전기한: plusDays(item.purchasedAt, meta.safeDeadlineDays),
    최대기한: plusDays(item.purchasedAt, meta.maxDeadlineDays),
    기한상태: deadlineText(item.waitingDays, meta.safeDeadlineDays, meta.maxDeadlineDays),
    상품교체: item.hasItemChange ? 'Y' : '',
    결제상태: paymentStatusLabel(item.paymentStatus),
    금액조정경위: item.paymentMemo ?? '',
    //* 홈택스에서 친 뒤 이 열에 표시해두고 화면에서 일괄 도장 찍는 흐름을 상정한 빈 칸
    발행여부: '',
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  const cols = [
    { wch: 12 }, // 주문번호
    { wch: 12 }, // 결제일
    { wch: 10 }, // 구분
    { wch: 12 }, // 발행금액
    { wch: 12 }, // 결제총액
    { wch: 12 }, // 교체일
    { wch: 12 }, // 포인트사용
    { wch: 12 }, // 주문자
    { wch: 16 }, // 전화번호
    { wch: 26 }, // 상품
    { wch: 6 }, // 수량
    { wch: 10 }, // 대기일수
    { wch: 12 }, // 안전기한
    { wch: 12 }, // 최대기한
    { wch: 14 }, // 기한상태
    { wch: 8 }, // 상품교체
    { wch: 10 }, // 결제상태
    { wch: 40 }, // 금액조정경위
    { wch: 10 }, // 발행여부
  ];
  ws['!cols'] = cols;
  ws['!freeze'] = { xSplit: '0', ySplit: '1' };
  if (rows.length > 0) {
    //* 마지막 컬럼 인덱스는 cols에서 파생 — 컬럼을 늘릴 때 필터가 잘리는 일을 막는다
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: rows.length, c: cols.length - 1 },
      }),
    };
  }
  //* 열 순서가 바뀌면 아래 좌표도 같이 틀어진다 — 헤더 이름으로 열을 찾아 고정한다
  const colOf = (header: string) => {
    const index = Object.keys(rows[0] ?? {}).indexOf(header);
    return index < 0 ? undefined : XLSX.utils.encode_col(index);
  };
  const moneyCols = ['발행금액', '결제총액', '포인트사용']
    .map(colOf)
    .filter((col): col is string => !!col);
  const phoneCol = colOf('전화번호');
  rows.forEach((_row, index) => {
    const r = index + 2; // 1행은 헤더
    moneyCols.forEach((col) => {
      const cell = ws[`${col}${r}`];
      if (cell && typeof cell.v === 'number') cell.z = '#,##0';
    });
    //* 전화번호는 반드시 텍스트로 — 숫자로 두면 앞 0이 사라진다
    const phone = phoneCol ? ws[`${phoneCol}${r}`] : undefined;
    if (phone) {
      phone.t = 's';
      phone.z = '@';
    }
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '현금영수증 발행대기');
  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([buffer], { type: 'application/octet-stream' }),
    `현금영수증_발행대기_${fmtDate(meta.range.from)}_${fmtDate(meta.range.to)}.xlsx`,
  );
};
